import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BuildingOfficeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const ClubSelection = ({ selectedProvince, onClubSelect, onBack, loading }) => {
  const [clubs, setClubs] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedProvince) {
      fetchClubs();
    }
  }, [selectedProvince]);

  const fetchClubs = async () => {
    try {
      setFetchLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/clubs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setClubs(response.data);
    } catch (err) {
      console.error('Error fetching clubs:', err);
      setError('Failed to load clubs. Please try again.');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleClubClick = (club) => {
    onClubSelect(club);
  };

  if (fetchLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-xl p-8 shadow-2xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading clubs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-xl p-8 shadow-2xl">
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchClubs}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-xl p-8 shadow-2xl">
        {/* Header with back button */}
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-gray-400 hover:text-white mr-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Select Your Club</h1>
            <p className="text-gray-400">
              Registering for: <span className="text-red-500 font-semibold">{selectedProvince?.name}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clubs.map((club) => (
            <button
              key={club.clubId}
              onClick={() => handleClubClick(club)}
              disabled={loading}
              className="bg-gray-700 hover:bg-gray-600 text-white rounded-lg p-6 
                       transition-all duration-300 transform hover:scale-105 
                       shadow-lg hover:shadow-xl flex flex-col items-center
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {club.logo ? (
                <img 
                  src={club.logo} 
                  alt={club.name} 
                  className="w-16 h-16 object-cover rounded-full mb-4 border-2 border-gray-600"
                />
              ) : (
                <BuildingOfficeIcon className="h-16 w-16 mb-4 text-red-500" />
              )}
              <h2 className="text-xl font-semibold mb-2 text-center">{club.name}</h2>
              {club.location && (
                <p className="text-gray-300 text-sm text-center mb-2">{club.location}</p>
              )}
              {club.foundedYear && (
                <p className="text-gray-400 text-xs">Founded: {club.foundedYear}</p>
              )}
            </button>
          ))}
        </div>

        {clubs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No clubs available for registration at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubSelection; 